import { useLocale } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { Locale } from '@module/mdrt-org/shared/utils/enums/locale';
import { logException } from '@shared/utils/helpers/error-logging';
import { useCssVariable } from '@shared/utils/hooks/use-css-variable';
type UsePdfFontLoaderResult = {
  boldFontBinaryString: null | string;
  isLoading: boolean;
  regularFontBinaryString: null | string;
};
export const localesUsingSpecialSymbols = [Locale.Ja, Locale.Ko, Locale.Th, Locale.ZhCHT, Locale.ZhCN, Locale.Vi];
const localesWithoutSpecialSymbols = [Locale.En, Locale.Es, Locale.Id];
const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let result = '';
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    result += String.fromCodePoint(...chunk);
  }
  return result;
};
export const usePdfFontLoader = (): UsePdfFontLoaderResult => {
  const locale = useLocale();
  const [PDF_FONT_FAMILY] = useCssVariable('--pdf-font-family');
  const fontDictionary = useMemo(() => ({
    [Locale.Ja]: {
      bold: '/fonts/NotoSansJP-Bold.ttf',
      regular: '/fonts/NotoSansJP-Regular.ttf'
    },
    [Locale.Ko]: {
      bold: '/fonts/NotoSansKR-Bold.ttf',
      regular: '/fonts/NotoSansKR-Regular.ttf'
    },
    [Locale.Th]: {
      bold: '/fonts/NotoSansThai-Bold.ttf',
      regular: '/fonts/NotoSansThai-Regular.ttf'
    },
    [Locale.Vi]: {
      bold: '/fonts/NotoSans-Bold.ttf',
      regular: '/fonts/NotoSans-Regular.ttf'
    },
    [Locale.ZhCHT]: {
      bold: '/fonts/NotoSansTC-Bold.ttf',
      regular: '/fonts/NotoSansTC-Regular.ttf'
    },
    [Locale.ZhCN]: {
      bold: '/fonts/NotoSansSC-Bold.ttf',
      regular: '/fonts/NotoSansSC-Regular.ttf'
    }
  }), []);
  const [isLoading, setIsLoading] = useState(true);
  const [regularFontBinaryString, setRegularFontBinaryString] = useState<null | string>(null);
  const [boldFontBinaryString, setBoldFontBinaryString] = useState<null | string>(null);
  useEffect(() => {
    let cancelled = false;
    if (localesWithoutSpecialSymbols.includes(locale as Locale)) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const loadFont = async (url: string, name: string, weight: FontFaceDescriptors['weight']) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch font: ${response.status}`);
      const buffer = await response.arrayBuffer();
      const face = new FontFace(name, buffer, {
        weight
      });
      await face.load();
      (document.fonts as unknown as {
        add: (f: FontFace) => void;
      }).add(face);
      return buffer;
    };
    const run = async () => {
      setIsLoading(true);
      setRegularFontBinaryString(null);
      setBoldFontBinaryString(null);
      try {
        const set = fontDictionary[locale as keyof typeof fontDictionary];
        const [regularBuffer, boldBuffer] = await Promise.all([loadFont(set.regular, PDF_FONT_FAMILY.propertyValue, 'normal'), loadFont(set.bold, PDF_FONT_FAMILY.propertyValue, 'bold')]);
        if (cancelled) return;
        setRegularFontBinaryString(arrayBufferToBinaryString(regularBuffer));
        setBoldFontBinaryString(arrayBufferToBinaryString(boldBuffer));
      } catch (error) {
        logException(error);
        if (!cancelled) {
          setRegularFontBinaryString(null);
          setBoldFontBinaryString(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [PDF_FONT_FAMILY.propertyValue, fontDictionary, locale]);
  return {
    boldFontBinaryString,
    isLoading,
    regularFontBinaryString
  };
};